import {
  Box,
  FieldWrapper,
  Switch,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { CustomFieldEntity } from 'apis/customFields';

import { AttributeFormFieldValues } from '../../../customAttributeModalForm.types';

import { emptyVisibilityCondition } from './constants';

const genericDescription =
  'Toggle whether to conditionally show this attribute to agents.';
const entityDescription: Record<CustomFieldEntity, string> = {
  conversation: `${genericDescription} Based on the value of another custom attribute or a conversation property.`,
  contact: `${genericDescription} Based on the value of another custom attribute or a contact property.`,
};

type ToggleVisibilityFormFieldProps = {
  /** The entity id. */
  entityId: string;
};

/** Represents the field for managing options of a custom select attribute. */
export const ToggleVisibilityFormField = ({
  entityId,
}: ToggleVisibilityFormFieldProps): JSX.Element => {
  // TODO: [TEAMS-13693] add `AttributeFormFieldValuesHavingOptions` generic back after updating to RHF v8
  const { control, setValue } =
    useFormContext<Omit<AttributeFormFieldValues, 'options'>>();

  const visibilityCondition = useWatch({
    control: control,
    name: 'visibility_condition',
  });
  const isRequiredToFill = useWatch({
    control: control,
    name: 'is_required',
  });
  const visibilityConditionEnabled = visibilityCondition !== null;

  const toggleVisibility = useCallback(() => {
    if (visibilityConditionEnabled) {
      // set visibility condition to null
      setValue('visibility_condition', null);
    } else {
      // set visibility condition to empty condition
      setValue('visibility_condition', emptyVisibilityCondition);
    }
  }, [setValue, visibilityConditionEnabled]);

  useEffect(() => {
    // set visibility condition empty if it the attribute is Required (We don't support both at the same time).
    if (isRequiredToFill && visibilityConditionEnabled) {
      setValue('visibility_condition', null);
    }
  }, [isRequiredToFill, setValue, visibilityConditionEnabled]);

  return (
    <FieldWrapper
      label="Toggle conditional visibility"
      labelFor="toggle_visibility"
    >
      <Box padding="0 0 250">
        <Text.Paragraph variant="4" subdued>
          {entityDescription[entityId] ?? genericDescription}
        </Text.Paragraph>
      </Box>

      <Tooltip
        content="Cannot set conditional visibility if field is required to fill"
        disabled={!isRequiredToFill}
        dark
      >
        <Switch
          checked={visibilityConditionEnabled}
          disabled={isRequiredToFill}
          onChange={toggleVisibility}
          name="toggle_visibility"
          value="toggle_visibility"
        />
      </Tooltip>
    </FieldWrapper>
  );
};
